<template>
  <!-- Единый слот уведомлений: показываем ОДИН баннер — самый приоритетный.
       Жёлтый = критично (оплата), синий = настройка/промо. -->
  <v-alert
    v-if="current"
    dense
    dark
    :color="current.severity === 'critical' ? 'warning' : 'primary'"
    :icon="current.icon || 'mdi-information-outline'"
    class="mb-2"
  >
    <div class="d-flex align-center flex-wrap" style="gap: 8px;">
      <span class="flex-grow-1" style="min-width: 200px;">{{ current.text }}</span>
      <v-btn v-if="current.to" small outlined :to="current.to">{{ current.actionLabel }}</v-btn>
      <v-btn
        v-else-if="current.href"
        small
        outlined
        :href="current.href"
        target="_blank"
        @click="dismiss(current)"
      >{{ current.actionLabel }}</v-btn>
      <v-btn v-else-if="current.event" small outlined @click="$emit(current.event)">{{ current.actionLabel }}</v-btn>
      <v-btn v-if="current.dismissible" small icon @click="dismiss(current)">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </div>
  </v-alert>
</template>

<script>
// Правила (см. обсуждение 2026-08-04):
// - один слот: очередь по приоритету = порядок в announcements;
// - критичные (оплата, billing address) и настроечные (часовой пояс, демо) —
//   только админам: остальные роли исправить не могут;
// - промо (Telegram) — не раньше 5-го входа, не на xs и не в нативном
//   приложении; закрытие постоянное (server-side флаг через App.closeBanner);
// - закрытия/отложки помнит localStorage per company.
import { Capacitor } from "@capacitor/core";

const SNOOZE_PREFIX = "rp:announce:";
const VISITS_KEY = "rp:announce:visits";
const PROMO_MIN_VISITS = 5;

export default {
  name: "AnnouncementBar",
  data() {
    return {
      // Инкремент после dismiss: localStorage нереактивен, дёргаем current вручную.
      snoozeTick: 0,
    };
  },
  created() {
    // Счётчик входов (одна сессия вкладки = один вход) — для отсрочки промо.
    try {
      if (!sessionStorage.getItem(`${SNOOZE_PREFIX}visited`)) {
        sessionStorage.setItem(`${SNOOZE_PREFIX}visited`, "1");
        const v = parseInt(localStorage.getItem(VISITS_KEY) || "0", 10) + 1;
        localStorage.setItem(VISITS_KEY, String(v));
      }
    } catch (e) {
      // private mode — промо просто не покажем
    }
  },
  computed: {
    company() {
      const c = this.$store.getters.getCompany;
      return c && c.company && c.company.id ? c.company : null;
    },
    mainCompany() {
      return this.$store.getters.getMainCompany || {};
    },
    currentUser() {
      return this.$store.getters.getCurrentUser || {};
    },
    currencySign() {
      return this.mainCompany.currency === "RUB" ? "₽" : "€";
    },
    isInvoiceClient() {
      return (
        this.mainCompany.payment_provider_preference === "paddle" &&
        this.mainCompany.plan &&
        this.mainCompany.plan.billing_collection_method === "invoice"
      );
    },
    isBillingAddressFilled() {
      const addr = this.company && this.company.billing_address;
      return !!(addr && addr.country_code && addr.city && addr.first_line && addr.postal_code);
    },
    billingAddressDue() {
      // Invoice-клиент без адреса, льготный период 7 дней с регистрации.
      if (!this.isInvoiceClient || this.isBillingAddressFilled) return false;
      const createdAt = this.mainCompany.created_at;
      if (!createdAt) return false;
      return (Date.now() - new Date(createdAt).getTime()) / 864e5 >= 7;
    },
    promoAllowed() {
      let visits = 0;
      try {
        visits = parseInt(localStorage.getItem(VISITS_KEY) || "0", 10);
      } catch (e) {
        return false;
      }
      return (
        !this.$vuetify.breakpoint.xsOnly &&
        !Capacitor.isNativePlatform() &&
        visits >= PROMO_MIN_VISITS
      );
    },
    announcements() {
      return [
        {
          id: "payment-due",
          severity: "critical",
          icon: "mdi-alert-circle-outline",
          // Не дублировать paywall-баннер (FT-070): когда тот виден, плашка о балансе лишняя.
          visible: this.isAdminPlus && !!this.mainCompany.id && this.mainCompany.payer_state === 2 && this.$store.getters.getPaywall.tier === "none",
          text: `${this.$t("app.system_balance")}: ${Math.round(this.mainCompany.balance || 0)}${this.currencySign}. ${this.$t("app.save_enter")}`,
          actionLabel: this.$t("custom.add_transfer"),
          to: { name: "Transactions" },
          dismissible: false,
        },
        {
          id: "billing-address",
          severity: "critical",
          icon: "mdi-alert-circle-outline",
          visible: this.isAdminPlus && this.billingAddressDue,
          text: this.$t("tariffs.billing_address_required_banner"),
          actionLabel: this.$t("tariffs.fill_billing_address"),
          to: { name: "Transactions" },
          dismissible: false,
        },
        // Баннер «выберите часовой пояс» убран (2026-08-04): пояс обязателен
        // при регистрации, а UTC бывает и осознанным выбором.
        {
          id: "demo-content",
          severity: "setup",
          icon: "mdi-flask-outline",
          visible:
            this.isAdminPlus &&
            !!this.company &&
            !!this.company.demo_content &&
            this.$store.getters.getBannerRemoveDemoContent,
          text: this.$t("demo_content.remove_demo_content_text_2"),
          actionLabel: this.$t("demo_content.remove"),
          event: "remove-demo",
          dismissible: true,
          snoozeDays: 7,
        },
        {
          id: "telegram",
          severity: "promo",
          icon: "mdi-send-circle-outline",
          visible:
            this.promoAllowed &&
            !!this.company &&
            this.$store.getters.getShowBanner &&
            !!this.currentUser.show_telegram,
          text: `${this.$t("telegram.banner_text")}. ${this.$t("telegram.banner_text_mobile")}`,
          actionLabel: this.$t("custom.join"),
          href: this.$i18n.locale === "ru" ? "https://t.me/rentprogcrm" : "https://t.me/rentprog_world",
          dismissible: true,
          dismissEvent: "dismiss-telegram",
          snoozeDays: 90,
        },
      ];
    },
    current() {
      this.snoozeTick; // зависимость для пересчёта после dismiss
      return this.announcements.find((a) => a.visible && !this.isSnoozed(a));
    },
  },
  methods: {
    snoozeKey(a) {
      return `${SNOOZE_PREFIX}${a.id}:${this.mainCompany.id || (this.company && this.company.id) || "0"}`;
    },
    isSnoozed(a) {
      if (!a.dismissible) return false;
      try {
        const until = localStorage.getItem(this.snoozeKey(a));
        return !!until && Date.now() < Number(until);
      } catch (e) {
        return false;
      }
    },
    dismiss(a) {
      if (a.snoozeDays) {
        try {
          localStorage.setItem(this.snoozeKey(a), String(Date.now() + a.snoozeDays * 864e5));
        } catch (e) {
          // ок: скроется до перезагрузки через snoozeTick
        }
      }
      if (a.dismissEvent) this.$emit(a.dismissEvent);
      this.snoozeTick += 1;
    },
  },
};
</script>
